import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShoppingCart } from "lucide-react";
import { sendPixelEvent, formatPhoneNumber } from "@/lib/pixelXApp";
import * as Sentry from '@sentry/react';

interface CheckoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CheckoutModal = ({ open, onOpenChange }: CheckoutModalProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    whatsapp: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Rastrear abertura do modal
  useEffect(() => {
    if (open) {
      Sentry.addBreadcrumb({
        category: 'ui',
        message: 'Checkout modal opened',
        level: 'info',
      });
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    Sentry.addBreadcrumb({
      category: 'form',
      message: 'Checkout form submitted',
      level: 'info',
      data: {
        hasName: !!formData.name,
        hasEmail: !!formData.email,
        hasWhatsapp: !!formData.whatsapp,
        nameLength: formData.name.length,
        whatsappLength: formData.whatsapp.length,
      },
    });
    
    try {
      // Validação
      if (!formData.name || !formData.email || !formData.whatsapp) {
        const missingFields = [];
        if (!formData.name) missingFields.push('name');
        if (!formData.email) missingFields.push('email');
        if (!formData.whatsapp) missingFields.push('whatsapp');
        
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // Formatar telefone
      const formattedPhone = formatPhoneNumber(formData.whatsapp);
      
      if (formattedPhone.length < 12 || formattedPhone.length > 13) {
        Sentry.captureMessage('Invalid phone number length', {
          level: 'warning',
          tags: { validation_error: 'phone' },
          contexts: {
            phone: {
              original: formData.whatsapp,
              formatted: formattedPhone,
              length: formattedPhone.length,
            },
          },
        });
      }

      // Adicionar contexto do usuário
      Sentry.setUser({
        email: formData.email,
        username: formData.name,
      });

      // Enviar evento Lead
      Sentry.addBreadcrumb({
        category: 'pixel',
        message: 'Sending Lead event',
        level: 'info',
      });

      await sendPixelEvent('Lead', {
        lead_name: formData.name,
        lead_email: formData.email,
        lead_phone: formattedPhone,
      });

      // Delay para garantir envio
      await new Promise(resolve => setTimeout(resolve, 500));

      // Redirecionar
      const checkoutUrl = "https://pxa.receitasem30minutos.com.br/exp/checkout";
      
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: 'Redirecting to checkout',
        level: 'info',
        data: { url: checkoutUrl },
      });
      
      window.location.href = checkoutUrl;

    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          form: 'checkout',
          step: 'submission',
        },
        contexts: {
          form: {
            hasName: !!formData.name,
            hasEmail: !!formData.email,
            hasWhatsapp: !!formData.whatsapp,
          },
        },
      });
      
      alert('Erro ao processar seu pedido. Por favor, tente novamente.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <ShoppingCart className="h-6 w-6 text-primary" />
            Finalizar Compra
          </DialogTitle>
          <DialogDescription>
            Preencha seus dados para garantir seu e-book por apenas R$ 29,90
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome completo</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Seu nome"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="seu@email.com"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="whatsapp">WhatsApp</Label>
            <Input
              id="whatsapp"
              name="whatsapp"
              type="tel"
              placeholder="(00) 00000-0000"
              value={formData.whatsapp}
              onChange={handleChange}
              className="pxa_mask_phone_inter"
              required
            />
          </div>
          
          <Button 
            type="submit"
            size="lg"
            className="w-full bg-gradient-warm hover:shadow-hover transition-all duration-300 text-lg h-12 mt-6"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processando...' : 'Continuar para pagamento'}
          </Button>
          
          <p className="text-xs text-center text-muted-foreground">
            Ao continuar, você será direcionado para a página de pagamento seguro
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutModal;
